import { theme, Tooltip, DeprecatedInfoIcon } from '@arnold/common';
import styled from '@emotion/styled/macro';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IReport } from '../../lib/reports/base';
import { ReactComponent as MetricQuestionIcon } from '../../assets/images/ChartResult.svg';
import { omit, uniq } from 'ramda';
import { Filter, getQuestionDefinitionByIndex } from '../../lib/reportHelper';
import PulserQuestionItem from './PulserQuestionItem';
import PulserOrderSelect, { OrderBy } from './PulserOrderSelect';
import { BarChartBody, ChartBar, ChartBarBox, TooltipContent, TooltipItem } from './StyledComponents';
import { ResponsiveSwitcher } from './ResponsiveSwitcher';
import { AggregatedData } from '../../lib/reports/aggregateData';
import { getTeamsForReversedGraph } from '../../lib/reports/queryAggregatedData';
import { calculateOverallQuestionStatistics, calculateAggregateStatistics } from './pulserHelpers';

type ReportDefinition = NonNullable<IReport['reportDefinition']>;

interface IProps {
  id: string;
  section: NonNullable<ReportDefinition['sections']>[0];
  sectionReport: IReport;
  compareLevelFilter: Filter | undefined;
  aggregatedData: AggregatedData | null;
  organizationId?: string;
}

const PulserSection: FC<IProps> = ({
  id,
  section,
  sectionReport,
  compareLevelFilter,
  aggregatedData,
  organizationId,
}) => {
  const { t } = useTranslation('PulserReport');
  const [orderBy, setOrderBy] = useState<OrderBy>(OrderBy.BEST_FIRST);
  const teams =
    compareLevelFilter && aggregatedData
      ? getTeamsForReversedGraph(aggregatedData, sectionReport, compareLevelFilter)
      : undefined;

  const questionsInWidgets = uniq(section.widgets?.map((widget) => widget.questionDefinitionIndex) ?? []);

  const questionsInSection = questionsInWidgets
    .map((questionIndex) => getQuestionDefinitionByIndex(sectionReport, questionIndex ?? ''))
    .filter((x): x is NonNullable<typeof x> => !!x);
  const showEscapeOptionInSection = questionsInSection.some((question) => question.showEscapeOption !== false);

  if (questionsInSection.length === 0) {
    return null;
  }

  const questionsWithResponses = questionsInSection.map((question) =>
    calculateOverallQuestionStatistics(sectionReport, question?.index ?? ''),
  );

  // Calculate aggregate statistics for all questions
  const {
    answers,
    averagePercentage: averageLevel,
    nonRelevantTotalCount,
  } = calculateAggregateStatistics(questionsWithResponses);

  // Sort questions based on selected order
  const sortedQuestions = questionsWithResponses.slice().sort((qA, qB) => {
    if (orderBy === OrderBy.WORST_FIRST) {
      return qA.percentage - qB.percentage;
    }
    return qB.percentage - qA.percentage;
  });

  const questionNodes = (
    <div className={'d-flex flex-column flex-grow-1 bg-white rounded-lg shadow-sm px-8 mb-3 '}>
      <div className="py-7 d-flex justify-content-end">
        <FilterText data-cy="report-pulser-order-by">
          {t('orderBy')}

          <PulserOrderSelect orderBy={orderBy} setOrderBy={setOrderBy} className=" ml-6" />
        </FilterText>
      </div>
      {sortedQuestions
        .reduce(
          (acc, question, index) => {
            // group the questions into groups of 5
            const groupIndex = Math.floor(index / 5);
            if (!acc[groupIndex]) {
              acc[groupIndex] = [];
            }
            acc[groupIndex].push(question);
            return acc;
          },
          [] as (typeof sortedQuestions)[0][][],
        )
        .map((questionGroup, questionGroupIndex, allQuestionGroups) => {
          return (
            <div data-pulser-question-group-index={questionGroupIndex}>
              {questionGroup.map((question, indexInGroup) => {
                const widget = section.widgets?.find((w) => w.questionDefinitionIndex === question.question?.index);
                return (
                  <PulserQuestionItem
                    data-pptx-no-border-bottom
                    key={question.question!.index}
                    questionText={widget?.headingLocal || question.question?.text || ''}
                    percentage={question.percentage}
                    question={question.question!}
                    sectionReport={sectionReport}
                    answerResults={question.answers}
                    notRelevantTotalCount={question.notRelevantResponses}
                    isLast={
                      allQuestionGroups.length - 1 === questionGroupIndex && indexInGroup === questionGroup.length - 1
                    }
                    teamsToShow={teams}
                    compareLevelFilter={compareLevelFilter}
                    aggregatedData={aggregatedData}
                    categoryPosition={questionGroupIndex * 5 + indexInGroup + 1}
                    organizationId={organizationId}
                  />
                );
              })}
            </div>
          );
        })}
    </div>
  );

  return (
    <Wrapper className={'mb-9'} id={id} data-cy={`report-pulser-section-${id}`}>
      <ResponsiveSwitcher
        mobile={
          <>
            <div className={'flex-grow-1 bg-white rounded-lg shadow-sm px-8 py-8 mb-3 '} data-pulser-total-score>
              <div className={'d-flex  justify-content-between align-items-center'}>
                <IconTextWrapper>
                  <MetricQuestionIcon width="28" height="28" />
                  <WidgetHeading className={'mb-4'} data-pptx-id="report-intro-title">
                    {t('totalScore')}
                  </WidgetHeading>
                  <WidgetHelp
                    onClick={() => {
                      window.open(t('totalScoreHelpUrl'), '_blank');
                    }}
                  >
                    <DeprecatedInfoIcon width="16" height="16" className="mr-4" />
                    {t('totalScoreHelp')}
                  </WidgetHelp>
                </IconTextWrapper>
                {Number.isFinite(averageLevel) && (
                  <PercentageValue data-no-break className={'text-center'}>
                    {averageLevel.toFixed(0)}%
                  </PercentageValue>
                )}
              </div>
              <BarChartBody width="100%">
                {answers
                  .filter((answer) => answer.percentage > 0)
                  .map((answer, index, array) => {
                    const previousLevels = array.slice(0, index).filter((a) => a.level === answer.level);
                    const percent = answer.percentage;
                    const color =
                      theme.colors.chart[answer.level as keyof typeof theme.colors.chart][previousLevels.length];
                    return (
                      <ChartBarBox key={index} width={percent}>
                        <ChartBar color={color} height="8px"></ChartBar>
                      </ChartBarBox>
                    );
                  })}
              </BarChartBody>
            </div>
            {questionNodes}
          </>
        }
        desktop={
          <>
            <div data-pulser-total-score>
              <div
                className={
                  'd-flex flex-grow-1 bg-white rounded-lg shadow-sm px-8 py-8 mb-3 justify-content-between align-items-start'
                }
                data-pulser-total-score-child
              >
                <IconTextWrapper data-no-break>
                  <MetricQuestionIcon width="28" height="28" />
                  <WidgetHeading className={'mb-4'} data-pptx-id="report-intro-title">
                    {t('totalScore')}
                  </WidgetHeading>
                  <WidgetHelp
                    onClick={() => {
                      window.open(t('totalScoreHelpUrl'), '_blank');
                    }}
                    data-pptx-hide
                    className="hide-from-export"
                  >
                    <DeprecatedInfoIcon width="16" height="16" className="mr-4" />
                    {t('totalScoreHelp')}
                  </WidgetHelp>
                </IconTextWrapper>
                <div className="d-flex align-items-center mt-7">
                  <div data-cy="report-pulser-overall-score-bar-chart">
                    <Tooltip
                      delay={100}
                      followCursor={false}
                      html={
                        <TooltipContent chartOptions={answers}>
                          {answers.map((option, index, array) => {
                            const count = option.responses;
                            const previousLevels = array.slice(0, index).filter((a) => a.level === option.level);
                            const highlightColor =
                              theme.colors.chart[option.level as keyof typeof theme.colors.chart][
                                previousLevels.length
                              ];
                            const percent = option.percentage;
                            return (
                              <TooltipItem key={index}>
                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: theme.spacing.c,
                                  }}
                                >
                                  <div
                                    style={{
                                      width: 8,
                                      height: 8,
                                      backgroundColor: highlightColor,
                                      borderRadius: '50%',
                                    }}
                                  ></div>
                                  <div style={{ fontWeight: 700 }}>{percent.toFixed(0)}%</div>
                                </div>
                                <div style={{ fontWeight: 700, marginLeft: theme.spacing.e }}>
                                  {t('numberOfAnswers', { count, postProcess: 'interval' })}
                                </div>
                                <div style={{ marginLeft: theme.spacing.e }}>{option.answer}</div>
                              </TooltipItem>
                            );
                          })}
                          {showEscapeOptionInSection && (
                            <TooltipItem>
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  alignItems: 'center',
                                  gap: theme.spacing.c,
                                }}
                              >
                                <div
                                  style={{
                                    width: 8,
                                    height: 8,
                                    backgroundColor: theme.colors.text.secondary,
                                    borderRadius: '50%',
                                  }}
                                ></div>
                                <div style={{ fontWeight: 700 }}>
                                  {t('numberOfAnswers', { count: nonRelevantTotalCount, postProcess: 'interval' })}
                                </div>
                              </div>
                              <div style={{ marginLeft: theme.spacing.e }}>{t('bubble:scaleEscapeOption')}</div>
                            </TooltipItem>
                          )}
                        </TooltipContent>
                      }
                    >
                      <BarChartBody width="300px">
                        {answers
                          .filter((answer) => answer.percentage > 0)
                          .map((answer, index, array) => {
                            const previousLevels = array.slice(0, index).filter((a) => a.level === answer.level);
                            const percent = answer.percentage;
                            const color =
                              theme.colors.chart[answer.level as keyof typeof theme.colors.chart][
                                previousLevels.length
                              ];
                            return (
                              <ChartBarBox key={index} width={percent}>
                                <ChartBar color={color} height="8px"></ChartBar>
                              </ChartBarBox>
                            );
                          })}
                      </BarChartBody>
                    </Tooltip>
                  </div>
                  {Number.isFinite(averageLevel) && (
                    <PercentageValue data-no-break className={'text-center'} data-cy="report-pulser-overall-score">
                      {averageLevel.toFixed(0)}%
                    </PercentageValue>
                  )}
                </div>
              </div>
              <PptxDivider data-pptx-only data-pptx-total-score-divider />
            </div>
            {questionNodes}
          </>
        }
      />
    </Wrapper>
  );
};

const IconTextWrapper = styled.div`
  max-width: calc(100% - 155px);
`;

const WidgetHeading = styled.div({
  ...omit(['family'], theme.typography.body.xLarge.bold),
  marginTop: theme.spacing.d,
  marginRight: theme.spacing.e,
  wordBreak: 'keep-all',
});
const WidgetHelp = styled.div({
  ...omit(['family'], theme.typography.body.small.link),
  marginTop: theme.spacing.d,
  marginRight: theme.spacing.e,
  wordBreak: 'keep-all',
  color: theme.colors.text.secondary,
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  transition: 'color 0.2s ease',
  '& path': {
    transition: 'fill 0.2s ease',
  },
  '&:hover': {
    color: theme.colors.text.primary,
  },
  '&:hover path': {
    fill: theme.colors.text.primary,
  },
});
const FilterText = styled.div({
  ...omit(['family'], theme.typography.body.medium.regular),
  color: theme.colors.text.secondary,
  '& > div': {
    width: '200px !important',
    display: 'inline-block',
  },
});

const PercentageValue = styled.div({
  ...omit(['family'], theme.typography.body.xLarge.bold),
  marginBottom: theme.spacing.b,
  marginRight: theme.spacing.e,
  marginLeft: theme.spacing.i,
  fontSize: '32px',
  wordBreak: 'keep-all',
});

const Wrapper = styled.div`
  scroll-margin-top: 80px;
`;

export default PulserSection;

const PptxDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${theme.colors.borderMain.default};
  opacity: 0.5;
  display: none;
`;
